import { Input, Spin, message } from 'antd'
import { useDebounceFn } from 'ahooks'
import { Trans, useLingui } from '@lingui/react/macro'
import stringWidth from 'string-width'

import type { $Enums } from '@prisma/client'

import TipsBaseModal from '@/pages/tad.ai/_blocks/Modals/Tips/TipsBaseModal'
import CreateButton from '@/pages/tad.ai/_components/CreateButton'
import ClipAudioContainer from '@/pages/tad.ai/ai-music-generator/_components/ClipAudioContainer'
import AudioRangeSelector from '@/pages/tad.ai/ai-music-generator/_components/AudioRangeSelector'
import { useAudio } from '@/hooks/audio/useAudio'
import { useReferenceUpload } from '@/pages/tad.ai/_components/UploadAudio/_hooks/useReferenceUpload'


import { useState, useEffect, useMemo } from 'react';
import { api } from '@/utils/api';
import { cls } from 'tagged-classnames-free';
function getInputWidth(input: string) {
  return stringWidth(input)
}

const MAX_FILENAME_WIDTH = 60

export interface EditAudioModalData {
  musicModel: Extract<$Enums.MusicModel, 'V2' | 'V3'>
  musicType: $Enums.MusicType
  token?: string
  filename: string
  file: string
  originFile?: string
  beginTime?: number
  endTime?: number
  /** Maximum selectable duration in seconds, defaults to 30 */
  maxDuration?: number
  /** Minimum required audio duration in seconds, defaults to 30 */
  minDuration?: number
  onSuccess?: (props: Pick<EditAudioModalData, 'token' | 'filename'>) => void
  onDetectedLyrics?: (lyrics: string) => void
  isNeedDetectLyrics?: boolean
}

export interface EditAudioModalProps {
  open: boolean
  data: EditAudioModalData
  onClose: () => void
}

function EditAudioModal(props: EditAudioModalProps) {
  const { open, data, onClose } = props
  const { t } = useLingui()
  const [uploading, setUploading] = useState(false)
  const [filename, setFilename] = useState('')
  const [touched, setTouched] = useState(false)
  const maxDuration = data.maxDuration ?? 30
  const minDuration = data.minDuration ?? 30

  const audio = useAudio({
    src: data?.file || '',
  })

  const { data: waveformData, isFetching: waveformLoading } =
    api.reference.waveform.useQuery(
      {
        file: data?.file || '',
      },
      {
        enabled: !!data?.file,
        retry: false,
        trpc: {
          context: {
            // Use a custom local message for waveform failures instead of global UNKNOWN_ERROR.
            skipError: true,
          },
        },
        onError: () => {
          message.error(
            "We couldn't process the uploaded audio file. Please check the file or try a different one.",
          )
        },
      },
    )

  const { handleReferenceUpload } = useReferenceUpload({
    setUploading,
    onSuccess: (token) => {
      data.onSuccess?.({
        token,
        filename,
      })
    },
    onDetectedLyrics: data.onDetectedLyrics,
  })

  const { mutateAsync: cropReference } = api.reference.crop.useMutation({
    onError: () => {
      setUploading(false)
    },
  })

  const [audioRange, setAudioRange] = useState<{
    start: number
    end: number
  }>({
    start: 0,
    end: 0,
  })

  const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds < 0) {
      seconds = 0
    }
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    const milliseconds = Math.floor((seconds % 1) * 1000)

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs}.${milliseconds.toString().padStart(3, '0')}`
  }

  useEffect(() => {
    if (open) {
      setFilename(data.filename || '')
      setTouched(false)
    } else {
      // 关闭弹窗时暂停音频播放
      audio.howlRef.current?.pause()
    }
  }, [data.filename, open, audio.howlRef])

  useEffect(() => {
    setAudioRange({
      start: data?.beginTime || 0,
      end: data?.endTime || Math.min(audio?.duration || 0, maxDuration),
    })
  }, [data?.beginTime, data?.endTime, audio?.duration, maxDuration])

  const { run: playAudioWhenRangeChange } = useDebounceFn(
    () => {
      audio.playDuration(audioRange.start, audioRange.end)
    },
    {
      wait: 500,
    },
  )

  const inputStatus = useMemo(() => {
    if (getInputWidth(filename) > MAX_FILENAME_WIDTH) return 'error'
    if (touched && !filename.trim()) return 'error'
    return undefined
  }, [filename, touched])

  const handleSave = async () => {
    if (!filename.trim() || getInputWidth(filename) > MAX_FILENAME_WIDTH) {
      return
    }
    if (audio && audio.duration) {
      if (audio.duration < minDuration) {
        message.error(t`Audio duration must be at least ${minDuration} seconds`)
        return
      }
      const start = audioRange.start
      const end = audioRange.end
      if (
        !Number.isFinite(start) ||
        !Number.isFinite(end) ||
        start < 0 ||
        end <= start
      ) {
        message.error(t`Invalid audio range, please re-select`)
        return
      }
      setUploading(true)
      const { cropAudioUrl } = await cropReference({
        file: data.file || '',
        start: formatTime(start),
        end: formatTime(end),
      })
      await handleReferenceUpload({
        musicType: data.musicType,
        // Keep the original audio URL so the next edit can start from source audio.
        originFile: data.originFile || data.file || undefined,
        file: cropAudioUrl,
        filename: filename.trim(),
        beginTime: start,
        endTime: end,
        musicModel: 'V3',
        isNeedDetectLyrics: data.isNeedDetectLyrics,
      })
    }
  }

  return (
    <TipsBaseModal open={open} onCancel={onClose} destroyOnHidden>
      <div>
        <div className={cls`text-f-text-secondary text-lg font-semibold`}>
          <Trans>Edit Reference Audio</Trans>
        </div>
        <div className={cls`mt-4`}>
          <div
            className={cls`text-f-text-secondary mb-2 text-sm font-semibold`}
          >
            <Trans>Signer name</Trans>
          </div>
          <Input
            placeholder={t`Enter a song name`}
            count={{
              show: true,
              max: MAX_FILENAME_WIDTH,
              strategy: getInputWidth,
            }}
            status={inputStatus}
            className='text-f-text-secondary h-14 !bg-transparent !px-4'
            size='large'
            value={filename}
            onChange={(e) => {
              setTouched(true)
              setFilename(e.target.value)
            }}
          />
        </div>
        <div className={cls`mb-6 mt-4`}>
          <div
            className={cls`text-f-text-secondary mb-2 text-sm font-semibold`}
          >
            <Trans>Select Audio</Trans>
          </div>
          <Spin spinning={waveformLoading}>
            <div className='bg-f-bg-elevated rounded-md'>
              <ClipAudioContainer
                playing={audio.playing}
                onClick={() => {
                  audio.togglePlayDuration(audioRange.start, audioRange.end)
                }}
              >
                <AudioRangeSelector
                  fileName={data?.filename || ''}
                  maxSelectDuration={maxDuration}
                  minSelectDuration={minDuration}
                  onRangeChange={(start, end) => {
                    setAudioRange({ start, end })
                    playAudioWhenRangeChange()
                  }}
                  audioDuration={audio.duration || 0}
                  playing={audio.playing}
                  audioCurrentTime={audio.currentTime}
                  waveformProps={{
                    audio: {
                      peaks: waveformData || [],
                      duration: audio?.duration || 0,
                    },
                  }}
                />
              </ClipAudioContainer>
            </div>
          </Spin>

          <div
            className={cls`text-f-text-tertiary mt-2 flex justify-end text-xs`}
          >
            {t`${Math.round(audioRange.end - audioRange.start)}s of the audio selected`}
          </div>
        </div>

        <CreateButton
          text={t`Save`}
          loading={uploading}
          onClick={handleSave}
          disabled={audio.loading}
        />
      </div>
    </TipsBaseModal>
  )
}

export default EditAudioModal
